import { Canvas, RootState } from '@react-three/fiber';
import React, { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHotkeys } from 'react-hotkeys-hook';
import { PCFSoftShadowMap, PerspectiveCamera, Vector3 } from 'three';
import { useSnapshot } from 'valtio';

import Backdrop from '@cartwhl/3d/r3f/components/Backdrop';
import Grid from '@cartwhl/3d/r3f/components/Grid';
import GroundPlane from '@cartwhl/3d/r3f/components/GroundPlane';
import Lights from '@cartwhl/3d/r3f/components/Lights';
import ManualRenderLoop from '@cartwhl/3d/r3f/components/ManualRenderLoop';
import ThreeWrapper from '@cartwhl/3d/threejs/ThreeWrapper';
import { isNonProduction } from '@cartwhl/common/utils/getEnvironment';

import useCanvasResizer from 'hooks/useCanvasResizer';
import {
  animationPlayerStore,
  characterPickerStore,
  navigationStore,
  posingStore,
  projectStore,
  sceneSettingsStore,
} from 'stores';
import { getAnimationPlayerStore } from 'stores/animationPlayerStoreFactory';
import { DEFAULT_POSE_CHARACTER_ID } from 'stores/characterPickerStore';
import { GizmoModeType } from 'stores/posingStore';

import CanvasError from '../AnimationPlayer/components/CanvasError';
import usePlayerHotkeys from '../AnimationPlayer/usePlayerHotkeys';
import ActiveCharacter from './ActiveCharacter';
import PoseEffects from './PoseEffects';
import PosingSystem from './lib/PosingSystem';

// Create a minimal pose-specific animation player store
const poseAnimationPlayerStore = getAnimationPlayerStore();

export default function PoseMainPlayer() {
  // #region DATA
  const {
    cameraSettings: { aspectRatio, serializedCamera },
    backgroundSettings: { showGrid, showGroundPlane, gridCellSize = 1 },
  } = useSnapshot(sceneSettingsStore.state, { sync: true });
  const cameraControls = animationPlayerStore.defaultInstance.cameraControls;

  const { currentProject } = useSnapshot(projectStore.state);

  // Make sure character list ready state is handled only once
  const { characters } = useSnapshot(characterPickerStore.state);
  const [isCharListReady, setIsCharListReady] = useState<boolean>(false);

  // const params = new URLSearchParams(window.location.search);
  const canvasStyle: React.CSSProperties = { borderRadius: 'inherit' };
  const savedCameraString = useRef<string>('');

  const isNonProductionEnvironment = isNonProduction();

  const {
    backgroundSettings: { useBackgroundGradient, hexColor },
  } = useSnapshot(sceneSettingsStore.state);

  // For use when we want to override the canvas size.
  // particularly useful for rendering mp4 videos at a specific resolution
  // if (params.has('canvasWidth') && params.has('canvasHeight')) {
  //   canvasStyle['width'] = parseInt(params.get('canvasWidth') ?? '1280');
  //   canvasStyle['height'] = parseInt(params.get('canvasHeight') ?? '960');
  // }
  // #endregion

  // #region HOTKEYS
  usePlayerHotkeys('main', poseAnimationPlayerStore, sceneSettingsStore);

  useHotkeys('r', () => {
    posingStore.state.gizmoMode = GizmoModeType.Rotate;
  });

  useHotkeys('t', () => {
    posingStore.state.gizmoMode = GizmoModeType.Translate;
  });

  useHotkeys('y', () => {
    posingStore.state.gizmoMode = GizmoModeType.zTwist;
  });

  useHotkeys('g', () => {
    posingStore.state.gizmoGlobalRootActive =
      !posingStore.state.gizmoGlobalRootActive;
  });
  // #endregion

  // #region HANDLERS
  const onCanvasLoaded = (s: RootState) => {
    console.log('Canvas - Loaded');
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    const env = new ThreeWrapper(s.scene, s.gl);

    // Set initial camera position & orbit target
    env.sphericalLook(0, 20, 8, [0, 0.5, 0]);

    // Update fiber's state with new camera & controller
    s.set({
      camera: env.camera as PerspectiveCamera,
      controls: env.camControl as unknown as RootState['controls'],
    });

    animationPlayerStore.setCameraControl(env.camControl);

    // Update scene settings when camera is moved
    env.camControl.addEventListener('rest', () => {
      const target = new Vector3();
      env.camControl.getTarget(target);
      const position = new Vector3();
      env.camControl.camera.getWorldPosition(position);

      const cameraProperties = {
        positionX: position.x,
        positionY: position.y,
        positionZ: position.z,
        targetX: target.x,
        targetY: target.y,
        targetZ: target.z,
      };
      savedCameraString.current = JSON.stringify(cameraProperties);
      sceneSettingsStore.set(
        'cameraSettings',
        'serializedCamera',
        JSON.stringify(cameraProperties),
      );
    });

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Create main system
    const ps = new PosingSystem(env);
    posingStore.state.posingSystem.current = ps;

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Reset any character to load. Project Loading will set this on success
    characterPickerStore.state.activeCharacterID = '';
    sceneSettingsStore.state.characterSettings.charUploadID = '';
  };

  // Execute after a frame has been renderered
  const onPostRender = () => {
    posingStore.state.posingSystem.current?.onPostRender();
  };
  // #endregion

  // #region EFFECTS
  useCanvasResizer(aspectRatio);

  // Set the initial camera position
  useEffect(() => {
    if (currentProject.data) {
      if (cameraControls) {
        if (serializedCamera) {
          if (serializedCamera !== savedCameraString.current) {
            const cameraDetails = JSON.parse(serializedCamera);

            cameraControls.setLookAt(
              cameraDetails.positionX,
              cameraDetails.positionY,
              cameraDetails.positionZ,
              cameraDetails.targetX,
              cameraDetails.targetY,
              cameraDetails.targetZ,
            );
          }
        }
      }
    }
  }, [currentProject, serializedCamera, cameraControls]);

  // Character List Ready State - Should only trigger once
  useEffect(() => {
    if (!isCharListReady && characters) {
      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      console.log('PoseMainPlayer - Characters list is now available');
      setIsCharListReady(true); // Set to true to avoid running this effect again.

      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      // NOTE: Loading project sometimes completes before the character list is
      // available. This is a problem since activeCharacter can not be set without
      // the character data objects available. To avoid this race condition, only
      // start loading projects after characters are available.
      const { projectID } = navigationStore.getRouteParams();
      if (projectID) {
        console.log('PoseMainPlayer - Loading Project');
        projectStore.loadCurrentProject(projectID).finally(() => {
          const id = sceneSettingsStore.state.characterSettings.charUploadID;
          if (id) {
            sceneSettingsStore.updateCharacter(id);
          } else {
            sceneSettingsStore.updateCharacter(DEFAULT_POSE_CHARACTER_ID);
          }
        });
      } else {
        console.log('PoseMainPlayer - project id is missing ', projectID);
      }
    }
  }, [characters]);

  // React component's onMount
  useEffect(() => {
    console.log('PoseMainPlayer - Mount');
    posingStore.init();
  }, []);
  // #endregion

  return (
    <div
      id="threejs-div"
      className={`transitiom-colors relative flex h-full w-full flex-none items-center justify-center overflow-hidden rounded-3xl ${
        aspectRatio === -1 ? '' : 'bg-gray-75 dark:bg-gray-60'
      }`}
    >
      <ErrorBoundary fallback={<CanvasError className="pb-16" />}>
        <Canvas
          id="r3f-canvas-holder"
          gl={{ preserveDrawingBuffer: true }}
          style={canvasStyle}
          shadows={{
            enabled: true,
            autoUpdate: true,
            type: PCFSoftShadowMap,
          }}
          onCreated={onCanvasLoaded}
        >
          <ManualRenderLoop onPostRender={onPostRender} />
          <Lights />
          <Backdrop
            useBackgroundGradient={useBackgroundGradient}
            hexColor={hexColor}
          />

          <ActiveCharacter />
          <PoseEffects />
          {showGrid && (
            <Grid cellSize={gridCellSize} sectionSize={gridCellSize * 5} />
          )}
          {isNonProductionEnvironment && showGroundPlane && <GroundPlane />}
        </Canvas>
      </ErrorBoundary>
    </div>
  );
}
